import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Checkbox,
  CardContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Crop as CropIcon,
  Computer as ComputerIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { themeService, Theme, ThemeServiceContext } from '../../services/themeService';
import customFrameService from '../../services/customFrameService';
import { userFrameService, UserFrame } from '../../services/userFrameService';
import * as api from '../../services/api';
import { CustomFrame } from '../../types';
import CustomFrameLayoutPreview from '../shared/CustomFrameLayoutPreview';
import UserPageShell, { userCardEnterSx } from '../shared/UserPageShell';
import ThemeMachineFilterDialog from './ThemeMachineFilterDialog';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL } from '../../config/api';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useI18n } from '../../i18n/useI18n';
import { getThemeFramePath, ThemeChannel } from '../../utils/themePaths';
import { rewriteToCdn } from '../../utils/cdn';

const LIMIT_PER_PAGE = 20;
const FRAME_SIZE_PRIORITY = ['2x4', '4x2', '2x6', '6x2', '4x6', '6x4', '3x4', '4x3'];

const normalizeFrameSize = (sizeValue?: string) =>
  String(sizeValue || '').trim().toLowerCase();

const getSizeColor = (size: string) => {
  const normalizedSize = normalizeFrameSize(size);
  const colors: Record<string, string> = {
    '2x4': '#2196f3',
    '4x2': '#f44336',
    '2x6': '#4caf50',
    '6x2': '#ff9800',
    '4x6': '#9c27b0',
    '6x4': '#607d8b',
    '3x4': '#e91e63',
    '4x3': '#00bcd4',
  };
  return colors[normalizedSize] || '#757575';
};

export interface ThemeDetailProps {
  channel?: ThemeChannel;
  userId?: string;
  embedded?: boolean;
}

const ThemeDetail: React.FC<ThemeDetailProps> = ({
  channel = 'pro',
  userId,
  embedded = false,
}) => {
  const { pages: t, formatMessage } = useI18n();
  const { themeId } = useParams<{ themeId: string }>();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const user = useSelector((state: RootState) => state.auth.user);
  const apiCtx: ThemeServiceContext = useMemo(
    () => (userId ? { adminUserId: userId, channel } : { channel }),
    [userId, channel],
  );
  const targetUserId = userId || user?._id;
  const [theme, setTheme] = useState<Theme | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [machineDialogOpen, setMachineDialogOpen] = useState(false);
  const [availableFrames, setAvailableFrames] = useState<CustomFrame[]>([]);
  const [loadingFrames, setLoadingFrames] = useState(false);
  const [frameSearch, setFrameSearch] = useState('');
  const [selectedFrameSizes, setSelectedFrameSizes] = useState<string[]>([]);
  const [addingFrameId, setAddingFrameId] = useState<string | null>(null);
  const [removingFrameId, setRemovingFrameId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFrame, setPickerFrame] = useState<CustomFrame | null>(null);
  const [pickerFrames, setPickerFrames] = useState<UserFrame[]>([]);
  const [pickerSelected, setPickerSelected] = useState<string[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerUploading, setPickerUploading] = useState(false);
  const pickerFileInputRef = useRef<HTMLInputElement | null>(null);
  const pickUserFramesOnAdd = true;

  useEffect(() => {
    if (themeId) {
      loadTheme();
    }
  }, [themeId, userId]);

  useEffect(() => {
    if (!themeId || userId) return;

    const socket: Socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      path: '/socket.io',
    });

    const joinUserRoom = () => socket.emit('join-user-room');
    socket.on('connect', joinUserRoom);
    socket.on('theme:content-changed', (data: {
      themeId?: string;
      changeType?: string;
      customFrameId?: string;
    }) => {
      if (data?.themeId !== themeId) return;

      if (data.changeType === 'custom-frame-removed' && data.customFrameId) {
        setTheme((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            customFrames: prev.customFrames.filter(
              (cf) => cf.customFrameId._id !== data.customFrameId
            ),
          };
        });
        return;
      }

      loadTheme();
    });

    return () => {
      socket.emit('leave-user-room');
      socket.off('connect', joinUserRoom);
      socket.off('theme:content-changed');
      socket.disconnect();
    };
  }, [themeId, userId]);

  const loadTheme = async () => {
    if (!themeId) return;

    try {
      setLoading(true);
      const response = await themeService.getTheme(themeId, apiCtx);
      setTheme(response.theme);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi tải chi tiết theme');
      toast.error('Lỗi khi tải chi tiết theme');
    } finally {
      setLoading(false);
    }
  };

  const themeCustomFrameIds = useMemo(() => {
    if (!theme) return new Set<string>();
    return new Set(theme.customFrames.map((cf) => cf.customFrameId._id));
  }, [theme]);

  const orderedSizes = useMemo(() => {
    const availableSizes = new Set(
      availableFrames.map((frame) => normalizeFrameSize(frame.size)).filter(Boolean)
    );
    const orderedByPriority = FRAME_SIZE_PRIORITY.map((size) => normalizeFrameSize(size));
    const remainingSizes = Array.from(availableSizes)
      .filter((size) => !orderedByPriority.includes(size))
      .sort((a, b) => a.localeCompare(b));
    return [...orderedByPriority.filter((s) => availableSizes.has(s)), ...remainingSizes];
  }, [availableFrames]);

  const filteredAvailableFrames = useMemo(() => {
    const q = frameSearch.trim().toLowerCase();
    const selectedSet =
      selectedFrameSizes.length > 0
        ? new Set(selectedFrameSizes.map((size) => normalizeFrameSize(size)))
        : null;

    return availableFrames.filter((f) => {
      if (themeCustomFrameIds.has(f._id)) return false;
      if (selectedSet && !selectedSet.has(normalizeFrameSize(f.size))) return false;
      if (!q) return true;
      return (
        f.name.toLowerCase().includes(q) ||
        normalizeFrameSize(f.size).includes(q)
      );
    });
  }, [availableFrames, frameSearch, themeCustomFrameIds, selectedFrameSizes]);

  const toggleFrameSizeFilter = (size: string) => {
    setSelectedFrameSizes((prev) =>
      prev.includes(size) ? prev.filter((item) => item !== size) : [...prev, size]
    );
  };

  const handleCustomFrameClick = (customFrameId: string) => {
    if (!themeId) return;
    navigate(getThemeFramePath(channel, themeId, customFrameId, userId));
  };

  const openAddDialog = async () => {
    if (!targetUserId) {
      toast.error('Không xác định được tài khoản. Vui lòng đăng nhập lại.');
      return;
    }

    setAddDialogOpen(true);
    setFrameSearch('');
    setSelectedFrameSizes([]);
    try {
      setLoadingFrames(true);
      let allFrames: CustomFrame[] = [];

      if (channel === 'lite') {
        const data = userId
          ? await api.getUserCustomFramesChannelGranted(userId, 'lite')
          : await api.getMyCustomFramesChannelGranted('lite');
        allFrames = (data.enabledFrames?.length
          ? data.enabledFrames
          : (data.grantedFrames || []).filter((frame) =>
              (data.enabledFrameIds || []).includes(String(frame._id)),
            )) as CustomFrame[];
      } else if (userId) {
        const data = await api.getUserCustomFramesChannelGranted(userId, 'pro');
        allFrames = (data.enabledFrames?.length
          ? data.enabledFrames
          : (data.grantedFrames || []).filter((frame) =>
              (data.enabledFrameIds || []).includes(String(frame._id)),
            )) as CustomFrame[];
      } else {
        let page = 1;
        let keepLoading = true;

        while (keepLoading) {
          const response = await customFrameService.getEnabledCustomFramesForUser({
            userId: targetUserId,
            isActive: true,
            page,
            limit: LIMIT_PER_PAGE,
          });

          const frames = response.frames || [];
          allFrames.push(...frames);

          if (response.pagination) {
            keepLoading = page < response.pagination.pages;
          } else {
            keepLoading = frames.length === LIMIT_PER_PAGE;
          }
          page += 1;
        }
      }

      setAvailableFrames(allFrames);
    } catch (err: any) {
      toast.error(err.message || err.response?.data?.message || 'Lỗi khi tải danh sách custom frame');
    } finally {
      setLoadingFrames(false);
    }
  };

  const handleAddCustomFrame = async (customFrameId: string, userFrameIds: string[] = []) => {
    if (!themeId) return;

    try {
      setAddingFrameId(customFrameId);
      const response = await themeService.addCustomFrameToTheme(themeId, customFrameId, apiCtx);
      if (userFrameIds.length > 0) {
        await themeService.addUserFrames({
          themeId,
          customFrameId,
          userFrameIds,
        }, apiCtx);
      }
      toast.success(
        userFrameIds.length > 0
          ? `Đã thêm khung và ${userFrameIds.length} user frame`
          : (response.message || 'Đã thêm custom frame vào theme'),
      );
      await loadTheme();
      setPickerOpen(false);
      setPickerFrame(null);
      setPickerSelected([]);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Lỗi khi thêm custom frame';
      toast.error(msg);
      await loadTheme();
    } finally {
      setAddingFrameId(null);
    }
  };

  const openUserFramePicker = async (frame: CustomFrame) => {
    setAddDialogOpen(false);
    setPickerFrame(frame);
    setPickerSelected([]);
    setPickerFrames([]);
    setPickerUploading(false);
    setPickerOpen(true);
    setPickerLoading(true);
    try {
      if (userId) {
        const assets = await api.getAdminUserCustomFrameAssets(
          userId,
          frame._id,
          'user_frame',
          1,
          200,
          channel,
        );
        setPickerFrames(
          (assets.items || []).map((item) => ({
            _id: item._id,
            customFrameId: item.customFrameId,
            userId: item.userId,
            name: item.name,
            description: item.description || '',
            frameImage: {
              url: item.frameImage?.url || '',
              path: item.frameImage?.path || '',
            },
            isActive: item.isActive,
            tags: item.tags || [],
            metadata: {
              originalFileName: item.metadata?.originalFileName || '',
              fileSize: item.metadata?.fileSize || 0,
              uploadDate: item.metadata?.uploadDate || item.createdAt,
            },
            customFrame: {
              _id: item.customFrameId,
              name: '',
              size: '',
              photoPositions: [],
            },
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
          })),
        );
      } else {
        const response = await userFrameService.getUserFrames({
          customFrameId: frame._id,
          limit: 100,
          channel,
        });
        setPickerFrames(response.userFrames || []);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Không tải được user frame');
      setPickerFrames([]);
    } finally {
      setPickerLoading(false);
    }
  };

  const closeUserFramePicker = () => {
    if (pickerUploading || addingFrameId) return;
    setPickerOpen(false);
    setAddDialogOpen(true);
  };

  const handlePickerUpload = async (picked: File[]) => {
    if (!pickerFrame || picked.length === 0) return;
    const files = picked.slice(0, 10);
    for (const file of files) {
      const isImage = file.type.startsWith('image/') || /\.(png|jpe?g)$/i.test(file.name);
      if (!isImage) {
        toast.error(`File ${file.name} không phải là file ảnh (JPEG, PNG)`);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File ${file.name} vượt quá giới hạn 5MB`);
        return;
      }
    }

    try {
      setPickerUploading(true);
      const created = userId
        ? (await api.uploadAdminUserCustomFrameAssets(
            userId,
            pickerFrame._id,
            'user_frame',
            files,
            channel,
          )).items.map((item) => ({
            _id: item._id,
            name: item.name,
            frameImage: { url: item.frameImage?.url || '', path: item.frameImage?.path || '' },
          }) as UserFrame)
        : ((await userFrameService.uploadUserFrames(pickerFrame._id, files, channel)).createdFrames || []) as UserFrame[];
      if (created.length > 0) {
        setPickerFrames((prev) => [...created, ...prev]);
        setPickerSelected((prev) => [
          ...created.map((frame) => String(frame._id)),
          ...prev,
        ]);
      }
      toast.success(`Đã upload ${created.length} user frame`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Upload user frame thất bại');
    } finally {
      setPickerUploading(false);
    }
  };

  const togglePickerFrame = (frameId: string) => {
    setPickerSelected((prev) =>
      prev.includes(frameId) ? prev.filter((id) => id !== frameId) : [...prev, frameId],
    );
  };

  const handleRemoveCustomFrame = async (
    e: React.MouseEvent,
    customFrameId: string
  ) => {
    e.stopPropagation();
    if (!themeId) return;

    try {
      setRemovingFrameId(customFrameId);
      const response = await themeService.removeCustomFrameFromTheme(themeId, customFrameId, apiCtx);
      toast.success(response.message || 'Đã xóa custom frame khỏi theme');
      if (response.theme) {
        setTheme(response.theme);
      } else {
        await loadTheme();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Lỗi khi xóa custom frame');
    } finally {
      setRemovingFrameId(null);
    }
  };

  if (loading) {
    return (
      <UserPageShell>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </UserPageShell>
    );
  }

  if (error || !theme) {
    return (
      <UserPageShell>
        <Alert severity="error">{error || t.themes.notFound}</Alert>
      </UserPageShell>
    );
  }

  return (
    <UserPageShell>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.25,
          mb: { xs: 1.5, sm: 2.5 },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            component="h1"
            fontWeight={800}
            noWrap
            title={theme.name}
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.85rem' },
              letterSpacing: '-0.03em',
              lineHeight: 1.2,
            }}
          >
            {theme.name}
          </Typography>
          {theme.tags.length > 0 && (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.6} sx={{ mt: 1 }}>
              {theme.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    bgcolor: alpha(muiTheme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                />
              ))}
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontWeight: 600 }}>
            {formatMessage(t.themes.metaCounts, {
              n: theme.metadata.totalCustomFrames,
              m: theme.metadata.totalUserFrames,
            })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignItems: 'center' }}>
          {(channel === 'pro' || channel === 'lite') && (
            <Button
              variant="outlined"
              startIcon={!isMobile ? <ComputerIcon /> : undefined}
              onClick={() => setMachineDialogOpen(true)}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 800,
                color: '#bf5f82',
                borderColor: alpha('#ff8ba7', 0.7),
                ...(isMobile ? { minWidth: 40, width: 40, height: 40, p: 0 } : { px: 2 }),
              }}
              aria-label={t.themes.machineFilterButton}
            >
              {isMobile ? <ComputerIcon /> : t.themes.machineFilterButton}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={!isMobile ? <AddIcon /> : undefined}
            onClick={openAddDialog}
            sx={{
              flexShrink: 0,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 800,
              boxShadow: 'none',
              ...(isMobile ? { minWidth: 40, width: 40, height: 40, p: 0 } : { px: 2 }),
            }}
            aria-label={t.themes.addCustomFrame}
          >
            {isMobile ? <AddIcon /> : t.themes.addCustomFrame}
          </Button>
        </Stack>
      </Box>

      {theme.themeImage && (
        <Paper
          elevation={0}
          sx={{
            mb: { xs: 2, sm: 3 },
            overflow: 'hidden',
            borderRadius: 3,
            border: '1px solid',
            borderColor: alpha(muiTheme.palette.primary.main, 0.14),
          }}
        >
          <CardMedia
            component="img"
            image={theme.themeImage.url}
            alt={theme.name}
            sx={{
              objectFit: 'cover',
              height: { xs: 160, sm: 240 },
              width: '100%',
              bgcolor: alpha(muiTheme.palette.primary.main, 0.06),
            }}
          />
        </Paper>
      )}

      <Typography
        fontWeight={800}
        sx={{ fontSize: { xs: '0.95rem', sm: '1.15rem' }, mb: { xs: 1.25, sm: 2 } }}
      >
        {t.themes.framesInTheme}
      </Typography>

      {theme.customFrames.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <CropIcon sx={{ fontSize: 52, color: 'text.secondary', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {t.themes.emptyFramesTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t.themes.emptyFramesHint}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
          >
            {t.themes.addCustomFrame}
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={{ xs: 1.25, sm: 2.5 }}>
          {theme.customFrames.map((customFrameData, index) => {
            const frame = customFrameData.customFrameId;
            return (
            <Grid item xs={6} sm={6} md={4} key={frame._id}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.25, sm: 2 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: alpha('#ff8ba7', 0.28),
                  boxShadow: `0 4px 14px ${alpha('#ff8ba7', 0.08)}`,
                  background: `linear-gradient(180deg, ${alpha('#fff', 0.98)} 0%, ${alpha('#ff8ba7', 0.08)} 100%)`,
                  position: 'relative',
                  overflow: 'visible',
                  ...userCardEnterSx(index),
                }}
                onClick={() => handleCustomFrameClick(frame._id)}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    zIndex: 1,
                    width: 30,
                    height: 30,
                    bgcolor: 'rgba(255,255,255,0.92)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    '&:hover': { bgcolor: 'error.main', color: 'white' },
                  }}
                  disabled={removingFrameId === frame._id}
                  onClick={(e) => handleRemoveCustomFrame(e, frame._id)}
                >
                  {removingFrameId === frame._id ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
                <Box sx={{ mt: { xs: 2.5, sm: 2 } }}>
                  <CustomFrameLayoutPreview
                    width={frame.width}
                    height={frame.height}
                    size={frame.size}
                    photoPositions={frame.photoPositions}
                    maxHeight={{ xs: 160, sm: 200 }}
                  />
                </Box>
                <Typography
                  fontWeight={700}
                  noWrap
                  title={frame.name}
                  sx={{
                    color: '#e86a8a',
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                    mt: 0.75,
                    textAlign: 'center',
                  }}
                >
                  {frame.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha('#e86a8a', 0.7),
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    textAlign: 'center',
                    fontWeight: 600,
                  }}
                >
                  {frame.size} • {customFrameData.userFrameIds.length} user frame
                </Typography>
              </Paper>
            </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 1.5, sm: 2 },
            width: { xs: 'calc(100% - 24px)', sm: '100%' },
            maxHeight: { xs: '88vh', sm: '90vh' },
            borderRadius: 3.5,
            overflow: 'hidden',
            backgroundImage: `linear-gradient(180deg, ${alpha(muiTheme.palette.primary.main, 0.07)} 0%, #fff 24%)`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.3rem' }, letterSpacing: '-0.02em', pt: 2.25, px: { xs: 2, sm: 3 } }}>
          {t.themes.addDialogTitle}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {channel === 'lite' ? t.themes.addDialogHintLite : t.themes.addDialogHint}
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={t.themes.searchPlaceholder}
            value={frameSearch}
            onChange={(e) => setFrameSearch(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                bgcolor: alpha(muiTheme.palette.primary.main, 0.05),
              },
            }}
          />
          {orderedSizes.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  mb: 1,
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                }}
              >
                {t.themes.filterByRatio}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant={selectedFrameSizes.length === 0 ? 'contained' : 'outlined'}
                  onClick={() => setSelectedFrameSizes([])}
                  sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
                >
                  {t.common.all}
                </Button>
                {orderedSizes.map((size) => (
                  <Button
                    key={size}
                    size="small"
                    variant={selectedFrameSizes.includes(size) ? 'contained' : 'outlined'}
                    onClick={() => toggleFrameSizeFilter(size)}
                    sx={{
                      borderRadius: 999,
                      textTransform: 'none',
                      fontWeight: 700,
                      boxShadow: 'none',
                      ...(selectedFrameSizes.includes(size)
                        ? {
                            bgcolor: getSizeColor(size),
                            borderColor: getSizeColor(size),
                            '&:hover': { bgcolor: getSizeColor(size), opacity: 0.9 },
                          }
                        : undefined),
                    }}
                  >
                    {size}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
          {loadingFrames ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredAvailableFrames.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              {availableFrames.length === 0
                ? (channel === 'lite' ? t.themes.noLiteFrames : t.themes.noProFrames)
                : t.themes.allAddedOrFiltered}
            </Typography>
          ) : (
            <Grid container spacing={{ xs: 1.25, sm: 2 }}>
              {filteredAvailableFrames.map((frame) => (
                <Grid item xs={6} sm={6} key={frame._id}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      height: '100%',
                    }}
                  >
                    <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
                      <CustomFrameLayoutPreview
                        width={frame.width}
                        height={frame.height}
                        size={frame.size}
                        photoPositions={frame.photoPositions}
                        maxHeight={{ xs: 110, sm: 130 }}
                      />
                      <Typography fontWeight={800} noWrap sx={{ fontSize: { xs: '0.78rem', sm: '0.9rem' }, mt: 0.75, color: '#e86a8a' }}>
                        {frame.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        {frame.size}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        disabled={addingFrameId === frame._id || pickerLoading}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!pickUserFramesOnAdd) {
                            handleAddCustomFrame(frame._id);
                            return;
                          }
                          window.setTimeout(() => openUserFramePicker(frame), 0);
                        }}
                        sx={{
                          mt: 1,
                          borderRadius: 999,
                          textTransform: 'none',
                          fontWeight: 800,
                          boxShadow: 'none',
                          minHeight: 32,
                        }}
                      >
                        {addingFrameId === frame._id ? <CircularProgress size={16} /> : t.common.add}
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Button
            onClick={() => setAddDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            {t.common.close}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pickerOpen}
        onClose={(_event, reason) => {
          if (addingFrameId || pickerUploading) return;
          if (reason === 'backdropClick' && document.activeElement === pickerFileInputRef.current) return;
          closeUserFramePicker();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 1.5, sm: 2 },
            width: { xs: 'calc(100% - 24px)', sm: '100%' },
            maxHeight: { xs: '88vh', sm: '90vh' },
            borderRadius: 3.5,
            overflow: 'hidden',
            backgroundImage: `linear-gradient(180deg, ${alpha(muiTheme.palette.primary.main, 0.07)} 0%, #fff 24%)`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.3rem' }, letterSpacing: '-0.02em', pt: 2.25, px: { xs: 2, sm: 3 } }}>
          {t.themes.pickUserFramesTitle}
          {pickerFrame ? ` — ${pickerFrame.name}` : ''}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t.themes.pickUserFramesHint}
          </Typography>
          <Paper
            elevation={0}
            onClick={() => {
              if (pickerUploading || pickerLoading || !pickerFrame) return;
              pickerFileInputRef.current?.click();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              mb: 2,
              borderRadius: 2.5,
              border: '1.5px solid',
              borderColor: alpha('#ff8ba7', 0.35),
              background: `linear-gradient(135deg, ${alpha('#ff8ba7', 0.18)} 0%, ${alpha('#ff8ba7', 0.08)} 100%)`,
              cursor: pickerUploading || pickerLoading ? 'default' : 'pointer',
              opacity: pickerUploading ? 0.7 : 1,
            }}
          >
            <input
              ref={pickerFileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,.png,.jpg,.jpeg"
              multiple
              style={{ display: 'none' }}
              disabled={pickerUploading || pickerLoading || !pickerFrame}
              onChange={(e) => {
                const files = e.target.files ? Array.from(e.target.files) : [];
                e.target.value = '';
                handlePickerUpload(files);
              }}
            />
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#ff8ba7', 0.35),
                color: '#e86a8a',
                flexShrink: 0,
              }}
            >
              {pickerUploading ? <CircularProgress size={22} sx={{ color: '#e86a8a' }} /> : <CloudUploadIcon />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: '#e86a8a', fontSize: '0.95rem' }}>
                {pickerUploading ? t.themes.pickUserFramesUploading : t.themes.pickUserFramesUpload}
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#e86a8a', 0.75) }}>
                {t.themes.pickUserFramesUploadHint}
              </Typography>
            </Box>
          </Paper>
          {pickerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : pickerFrames.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              {t.themes.pickUserFramesEmpty}
            </Typography>
          ) : (
            <Grid container spacing={{ xs: 1.25, sm: 2 }}>
              {pickerFrames.map((frame) => {
                const selected = pickerSelected.includes(frame._id);
                const imageUrl = frame.frameImage?.url ? rewriteToCdn(frame.frameImage.url) : '';
                return (
                  <Grid item xs={6} sm={6} md={4} key={frame._id}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        borderRadius: 2.5,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        boxShadow: selected
                          ? `0 8px 18px ${alpha(muiTheme.palette.primary.main, 0.2)}`
                          : 'none',
                        overflow: 'hidden',
                      }}
                      onClick={() => togglePickerFrame(frame._id)}
                    >
                      <CardMedia
                        sx={{
                          height: { xs: 120, sm: 150 },
                          bgcolor: alpha(muiTheme.palette.primary.main, 0.05),
                          backgroundSize: 'contain',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                          position: 'relative',
                        }}
                        image={imageUrl}
                        title={frame.name}
                      >
                        <Checkbox
                          checked={selected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => togglePickerFrame(frame._id)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'rgba(255,255,255,0.92)',
                            borderRadius: '50%',
                            p: 0.4,
                          }}
                        />
                      </CardMedia>
                      <CardContent sx={{ py: 1, px: 1, '&:last-child': { pb: 1 } }}>
                        <Typography
                          noWrap
                          fontWeight={700}
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, textAlign: 'center' }}
                        >
                          {frame.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={closeUserFramePicker}
            disabled={Boolean(addingFrameId) || pickerUploading}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999 }}
          >
            {t.common.cancel}
          </Button>
          {pickerFrames.length === 0 ? (
            <Button
              variant="contained"
              disabled={!pickerFrame || Boolean(addingFrameId) || pickerLoading || pickerUploading}
              onClick={() => pickerFrame && handleAddCustomFrame(pickerFrame._id)}
              sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, boxShadow: 'none' }}
            >
              {addingFrameId ? <CircularProgress size={16} /> : t.themes.pickUserFramesAddFrame}
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={!pickerFrame || Boolean(addingFrameId) || pickerUploading}
              onClick={() => pickerFrame && handleAddCustomFrame(pickerFrame._id, pickerSelected)}
              sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 999, boxShadow: 'none' }}
            >
              {addingFrameId
                ? <CircularProgress size={16} />
                : formatMessage(t.themes.pickUserFramesAdd, { n: pickerSelected.length })}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {(channel === 'pro' || channel === 'lite') && (
        <ThemeMachineFilterDialog
          open={machineDialogOpen}
          theme={theme}
          channel={channel}
          apiCtx={apiCtx}
          userId={userId}
          onClose={() => setMachineDialogOpen(false)}
          onSaved={(next) => {
            setTheme(next);
            setMachineDialogOpen(false);
          }}
        />
      )}
    </UserPageShell>
  );
};

export default ThemeDetail;
